//
// # pathInput
//
// Shared helpers for the design-path text input used by the create
// (DesignNameDialog) and Save As (SaveAsDialog) dialogs.
//
// A design ident is a `HierPathIdent` whose `path` is an arbitrary-length list
// of segments, e.g. ["library", "cell", "name"]. The dialogs let the user type
// the *full* design path as a single slash-separated string like
// "library/cell/name"; it is stored verbatim. The design's type is its
// contentKind, not a path segment.
//

import type { schema } from "GenAlphaSchema";
import type { SaveAsDestination } from "../../types";

/**
 * The ident a Save As writes to. Used by both the dialog's preview and the save,
 * so the shown address is the saved one.
 *
 * Depot: the full path verbatim. OpenAccess: (lib, leaf as cell, view); the
 * parent path is dropped. Lives here, not in `workspace.ts`, so tests can reach
 * it without the editor.
 */
export function saveAsTargetIdent(
  fullPath: ReadonlyArray<string>,
  destination: SaveAsDestination
): schema.Ident {
  return destination.kind === "openaccess"
    ? oaCellviewIdent(
        destination.lib,
        fullPath[fullPath.length - 1] ?? "",
        destination.view
      )
    : { kind: "hierpath", path: [...fullPath] };
}

/**
 * The ident of an OpenAccess cellview. Every form-to-ident conversion goes
 * through this so the shown and written addresses trim identically.
 */
export function oaCellviewIdent(
  lib: string,
  cell: string,
  view: string
): schema.LibCellViewIdent {
  return {
    kind: "libcellview",
    lib: lib.trim(),
    cell: cell.trim(),
    view: view.trim(),
  };
}

// Each path segment must be alphanumeric, underscore, or hyphen.
// Matches the VSCode extension validation pattern.
export const NAME_PATTERN = /^[a-zA-Z0-9_-]+$/;

// Parse a slash-separated path string into its segments. Trims each segment and
// drops empties so leading/trailing/duplicate slashes are tolerated:
//   "  a / b//c/ " -> ["a", "b", "c"]
export function parsePathInput(input: string): string[] {
  return input
    .split("/")
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
}

// A path input is valid when it has at least one segment and every segment
// matches NAME_PATTERN.
export function isValidPathInput(input: string): boolean {
  const segments = parsePathInput(input);
  return segments.length > 0 && segments.every((s) => NAME_PATTERN.test(s));
}

// A single leaf segment is valid when (trimmed) it matches NAME_PATTERN. Used
// for the separate "Name" field in the create / Save As dialogs.
export function isValidSegment(segment: string): boolean {
  return NAME_PATTERN.test(segment.trim());
}

// Combine a slash-separated parent path and a single leaf name into the full
// design-path segments. The parent is parsed like any path input; the leaf is
// appended as one trimmed segment. Callers validate the parts first.
export function buildDesignPath(parent: string, leaf: string): string[] {
  return [...parsePathInput(parent), leaf.trim()];
}

// Render a path segment list back into the slash-separated display form.
export function formatPath(path: ReadonlyArray<string>): string {
  return path.join("/");
}

// Per-path clash detection. True when the candidate full design path already
// exists in `existing` — a set of full ident path strings joined with "/".
// A path holds exactly one design (its type is its contentKind), so any
// existing design at the same path clashes regardless of type.
export function pathClashes(
  existing: ReadonlySet<string>,
  segments: ReadonlyArray<string>
): boolean {
  return existing.has(formatPath(segments));
}
